import { useCallback, useEffect, useRef, useState } from "react";
import type { PdfDocStatus, TextItem } from "./types";

/**
 * All pdf.js usage lives in this one hook — nowhere else in PdfProvenance
 * touches the `pdfjs-dist` package directly. pdf.js is loaded via a
 * dynamic `import("pdfjs-dist")` *inside an effect*, deliberately, for two
 * independent reasons:
 *
 *  1. Bundle size. pdf.js (plus the worker script it ships) is one of the
 *     largest packages in this repo's dependency tree. A static
 *     `import "pdfjs-dist"` at module scope would pull the whole library
 *     into whatever chunk imports PdfProvenance, even for routes that
 *     never render one. A dynamic import() is webpack's signal to split
 *     it into its own chunk that only downloads once a PdfProvenance
 *     instance actually mounts.
 *  2. Jest. react-scripts' jest config runs source through babel, not
 *     webpack — a static import has no code-splitting benefit there, it's
 *     just eagerly required. pdfjs-dist does canvas/worker
 *     feature-detection at module-load time that doesn't play well with
 *     jsdom. Keeping the import inside a `useEffect` means a test file
 *     that never mounts a PdfProvenance (e.g. textMatch.test.ts, which
 *     only wants the pure matcher in ./textMatch) never causes pdfjs-dist
 *     to be `require`'d at all — jest's module graph simply never reaches
 *     it, so it needs no jest mocking/config of its own.
 *
 * Worker file: pdf.js insists on running its parser off the main thread,
 * which means a worker script has to be reachable as a plain static URL
 * (it can't itself be bundled/dynamically-imported the normal way under
 * CRA5/webpack5 + this pinned pdfjs-dist version). We copy
 * node_modules/pdfjs-dist/build/pdf.worker.min.js to public/pdf.worker.min.js
 * as a committed static asset and point GlobalWorkerOptions.workerSrc at
 * it. package.json's "postinstall" script (`cp
 * node_modules/pdfjs-dist/build/pdf.worker.min.js public/pdf.worker.min.js`)
 * keeps that copy version-locked to the installed pdfjs-dist automatically
 * on every `yarn install` — no more hand-copying after a version bump. It's
 * still a plain file copy, not a symlink: `yarn install` has to actually run
 * (and re-run) for a pdfjs-dist upgrade to propagate, and pdf.js hard-errors
 * if the API and worker versions ever do drift out of lockstep.
 */

// Demo documents are 1-3 pages; this is a guard against a pathological
// upload, not a feature. Only the up-front warm-up scan below is capped —
// getTextItems() still lazily extracts any page on demand (including one
// past this cap) when something later asks for it directly, e.g. a filmstrip
// click. What the cap actually limits is hasTextLayer's own page range and
// the initial per-page cache warm: a document whose first 20 pages are
// blank/scanned but which has real text on page 21+ would be (wrongly)
// reported as having no text layer at all, and a value that ONLY appears
// past page 20 won't be pre-cached — it will still resolve correctly once
// something calls getTextItems for that specific page, just not for free as
// part of the initial load.
const TEXT_EXTRACT_PAGE_CAP = 20;

// Backing-store AREA budget for one page canvas. The caller's MAX_CANVAS_PX
// bounds the WIDTH only, and a portrait page is ~1.41x taller than wide: a
// 600 px pane at dpr 2 with the 3x zoom store came to 3600x5091 = 18.3 M px,
// and iOS/iPadOS Safari refuses any canvas over 16,777,216 px (2^24), which
// draws a blank page. 2^23 is half that hard limit, so Math.ceil rounding or
// a dpr change mid-session can never cross it, and at 32 MB RGBA it leaves
// room under Safari's total canvas-memory ceiling for the thumbnails and a
// second viewer on the same screen. Only the zoom's extra sharpness pays:
// the store never drops below plain devicePixelRatio, which is what every
// page rendered at before hover zoom.
const MAX_BACKING_PX = 2 ** 23;

/** The `pixelScale` a `cssWidth` x `cssHeight` page may actually render at:
 *  as asked, but lowered so the backing store stays within MAX_BACKING_PX,
 *  and never below 1. */
export function capPixelScale(
  cssWidth: number,
  cssHeight: number,
  dpr: number,
  pixelScale: number
): number {
  const fit = Math.sqrt(MAX_BACKING_PX / (cssWidth * dpr * cssHeight * dpr));
  return Math.max(1, Math.min(pixelScale, fit));
}

export interface UsePdfDocumentResult {
  status: PdfDocStatus;
  pageCount: number;
  /** false when every page scanned during load (see TEXT_EXTRACT_PAGE_CAP)
   * returned zero (non-blank) text items — i.e. a scanned/rasterized
   * document with no text layer to search. */
  hasTextLayer: boolean;
  /** Render `pageNumber` into `canvas`, fit to `cssWidth` (CSS pixels),
   * at devicePixelRatio-aware backing resolution. `pixelScale > 1` renders
   * a sharper backing store at the same CSS size, so the page can be zoomed
   * without blurring. Resolves with the rendered CSS size, which the caller
   * needs to size/position an overlay on top of the canvas. */
  renderPage: (
    pageNumber: number,
    canvas: HTMLCanvasElement,
    cssWidth: number,
    pixelScale?: number
  ) => Promise<{ width: number; height: number }>;
  /** Page-relative (0..1) text items for `pageNumber`, cached after the
   * first read. */
  getTextItems: (pageNumber: number) => Promise<TextItem[]>;
}

// Narrow local shape for the bits of pdf.js's API surface this hook uses.
// Kept as `any`-backed rather than importing pdfjs-dist's own types at the
// top of the file — doing so would require a type-only import, which is
// harmless for tsc but easy to accidentally upgrade into a value import
// later. Isolating ALL pdfjs contact (types included) to inside this
type PdfjsModule = any;
type PdfDocumentProxy = any;
type PdfPageProxy = any;
type PdfLoadingTask = any;
type PdfRenderTask = any;

/**
 * The ONE place every pdf.js `getDocument` call takes its parameters from.
 *
 * `isEvalSupported: false` closes CVE-2024-4367 (arbitrary JavaScript
 * execution from a crafted PDF's font program) on the vendored pdf.js
 * 3.11.174. The advisory names this option as the workaround for every
 * version below 4.2.67. The cost is that font glyph paths and PostScript
 * (Type 4) functions are interpreted rather than compiled, which is slower
 * for font-heavy documents but renders the same.
 * The pdfjs-dist 4.x upgrade is on the roadmap and will retire the need for
 * the flag, but keep it until then: never call `getDocument` without it
 * (noBareGetDocument.test.ts enforces this across src/).
 */
export const documentParams = (data: ArrayBuffer): { data: ArrayBuffer; isEvalSupported: false } => ({
  data,
  isEvalSupported: false,
});

/**
 * Load pdf.js as a plain <script> from public/ — the SAME delivery mechanism
 * as pdf.worker.min.js below it. This replaces a dynamic
 * `import("pdfjs-dist")`, which the Cloud Run image build shipped VERBATIM
 * (un-bundled) on 2026-08-25: the browser then tried to natively resolve the
 * bare specifier, threw, and every document fell into the "Couldn't render"
 * fallback while local/bucket builds of the same commit were fine. The
 * worker file survived every pipeline because it is a static file in
 * public/, so pdf.min.js now ships the same way (postinstall copies both,
 * both are committed). Lazy-loading is preserved — nothing fetches until the
 * first document renders — and the UMD build assigns window.pdfjsLib.
 */
let pdfjsLoadPromise: Promise<PdfjsModule> | null = null;

export function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsLoadPromise) {
    pdfjsLoadPromise = new Promise<PdfjsModule>((resolve, reject) => {
      const existing = (window as unknown as { pdfjsLib?: PdfjsModule }).pdfjsLib;
      if (existing) {
        resolve(existing);
        return;
      }
      const el = document.createElement("script");
      el.src = `${process.env.PUBLIC_URL || ""}/pdf.min.js`;
      el.async = true;
      el.onload = () => {
        const lib = (window as unknown as { pdfjsLib?: PdfjsModule }).pdfjsLib;
        if (lib) {
          if (!lib.GlobalWorkerOptions?.workerSrc) {
            lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
          }
          resolve(lib);
        } else reject(new Error("pdf.min.js loaded but window.pdfjsLib is missing"));
      };
      el.onerror = () => {
        // Clear the memo so a transient network failure can retry on the
        // next document instead of poisoning the whole session.
        pdfjsLoadPromise = null;
        reject(new Error("failed to load pdf.min.js"));
      };
      document.head.appendChild(el);
    });
  }
  return pdfjsLoadPromise;
}

/**
 * Convert pdf.js's raw text-content items — positioned via a text matrix
 * in PDF user space — into page-relative 0..1 rects (see types.ts for why
 * 0..1 rather than device pixels).
 *
 * Exported (rather than kept private to the hook) so a test can run the
 * REAL conversion against a REAL PDF page — see
 * __tests__/sampleDocument.test.ts. Duplicating this math in a test would
 * defeat the point of the test.
 */
export async function extractTextItems(page: PdfPageProxy, pdfjs: PdfjsModule): Promise<TextItem[]> {
  const viewport = page.getViewport({ scale: 1 });
  const { items } = await page.getTextContent();
  const out: TextItem[] = [];

  for (const raw of items) {
    // TextMarkedContent entries (present only when includeMarkedContent
    // is requested, which we don't) have no `str`/`transform` — this
    // guard is really just narrowing the union type for tsc.
    if (!("str" in raw) || !("transform" in raw)) continue;
    if (typeof raw.str !== "string" || raw.str.trim() === "") continue;

    // Combine the item's own text matrix with the viewport's transform to
    // get its position in the same top-left/y-down pixel space the
    // viewport (and therefore our rendered canvas) uses. tx = [a,b,c,d,e,f]:
    // (a,b) is the glyph run's x-axis, (c,d) its y-axis, (e,f) its
    // baseline origin.
    const tx = pdfjs.Util.transform(viewport.transform, raw.transform) as number[];
    const fontHeight = Math.hypot(tx[2], tx[3]);
    if (fontHeight <= 0) continue;

    const left = tx[4];
    // The baseline sits near the bottom of the glyph box; approximating
    // the ascent as the full font height slightly over-extends the box
    // above the ink. That's a deliberate, harmless bias for a highlight
    // box — better to be a touch generous than to clip ascenders.
    const top = tx[5] - fontHeight;
    const width = Math.abs(raw.width);

    out.push({
      str: raw.str,
      x: left / viewport.width,
      y: top / viewport.height,
      w: width / viewport.width,
      h: fontHeight / viewport.height,
    });
  }

  return out;
}

export function usePdfDocument(file: File | null): UsePdfDocumentResult {
  const [status, setStatus] = useState<PdfDocStatus>("loading");
  const [pageCount, setPageCount] = useState(0);
  const [hasTextLayer, setHasTextLayer] = useState(false);

  // Mutable refs so the imperative methods below always see the latest
  // doc/module/cache without needing to be re-created (and without making
  // every consumer effect re-run) every time status/pageCount changes.
  const docRef = useRef<PdfDocumentProxy | null>(null);
  const pdfjsRef = useRef<PdfjsModule | null>(null);
  const textCacheRef = useRef<Map<number, TextItem[]>>(new Map());
  // Bumped on every new `file`; guards against a stale async load (from a
  // previously-dropped file) finishing after a new one has started and
  // clobbering state that no longer corresponds to the current prop.
  const generationRef = useRef(0);
  // The loading task owns both the PDFDocumentProxy *and* its dedicated
  // web worker; only destroy() releases them. Held in a ref so cleanup can
  // tear it down even while it's still loading.
  const loadingTaskRef = useRef<PdfLoadingTask | null>(null);
  // pdf.js rejects a second render() into a canvas that still has one in
  // flight ("Cannot use the same canvas during multiple render()
  // operations"), and a resize drag or a filmstrip click routinely lands
  // mid-render. Three refs, keyed by target canvas, keep that safe:
  //   tasks - the render currently painting, so it can be cancelled;
  //   queue - a promise chain that serialises renders into one canvas, so
  //           a new one never starts before the old one has settled;
  //   seq   - the newest request id, so a render still sitting in the
  //           queue when a newer one arrives bails instead of painting a
  //           frame that's already stale.
  const renderTasksRef = useRef<Map<HTMLCanvasElement, PdfRenderTask>>(new Map());
  const renderQueueRef = useRef<Map<HTMLCanvasElement, Promise<unknown>>>(new Map());
  const renderSeqRef = useRef<Map<HTMLCanvasElement, number>>(new Map());

  useEffect(() => {
    // Captured up front purely to satisfy react-hooks/exhaustive-deps. The
    // maps are created once by useRef and never reassigned, so these are
    // the same objects the cleanup would have read off the refs.
    const renderTasks = renderTasksRef.current;
    const renderQueue = renderQueueRef.current;
    const renderSeq = renderSeqRef.current;

    const generation = ++generationRef.current;
    textCacheRef.current = new Map();
    docRef.current = null;
    setStatus("loading");
    setPageCount(0);
    setHasTextLayer(false);

    if (!file) return;

    let cancelled = false;

    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        // Set at load time (not module-eval time) so it always reflects
        // the currently-running app's PUBLIC_URL — matching wherever CRA
        // actually copied public/ assets for this build (root, or a
        // sub-path deploy).
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
        pdfjsRef.current = pdfjs;

        const buffer = await file.arrayBuffer();
        if (cancelled || generation !== generationRef.current) return;

        const loadingTask = pdfjs.getDocument(documentParams(buffer));
        loadingTaskRef.current = loadingTask;
        const doc = await loadingTask.promise;
        if (cancelled || generation !== generationRef.current) return;

        docRef.current = doc;

        // Scan every page's text content up front (capped, see
        // TEXT_EXTRACT_PAGE_CAP above) rather than lazily inferring
        // hasTextLayer from page 1 alone. These are short demo documents (a
        // handful of pages), so the cost is negligible, and it means a
        // document whose first page is a cover image but whose later pages
        // have real text isn't wrongly reported as scanned. This also warms
        // the per-page cache for every scanned page, not just the one
        // initially viewed.
        let anyText = false;
        const warmupPageCount = Math.min(doc.numPages, TEXT_EXTRACT_PAGE_CAP);
        for (let pageNumber = 1; pageNumber <= warmupPageCount; pageNumber++) {
          const page = await doc.getPage(pageNumber);
          const items = await extractTextItems(page, pdfjs);
          if (cancelled || generation !== generationRef.current) return;
          textCacheRef.current.set(pageNumber, items);
          if (items.length > 0) anyText = true;
        }

        setPageCount(doc.numPages);
        setHasTextLayer(anyText);
        setStatus("ready");
      } catch {
        if (!cancelled && generation === generationRef.current) {
          setStatus("failed");
        }
      }
    })();

    return () => {
      cancelled = true;
      // Cancel any painting before the document that owns those pages goes
      // away, otherwise pdf.js keeps the page's operator list alive until
      // the render settles.
      renderTasks.forEach((task) => task.cancel());
      renderTasks.clear();
      renderQueue.clear();
      renderSeq.clear();
      const task = loadingTaskRef.current;
      loadingTaskRef.current = null;
      docRef.current = null;
      // destroy() tears down the document AND its dedicated worker. Without
      // it, every dropped file leaks a worker plus the whole parsed doc for
      // the lifetime of the tab. It rejects if the load never completed,
      // which is exactly the case we're cleaning up after.
      void task?.destroy().catch(() => undefined);
    };
  }, [file]);

  const doRenderPage = useCallback(
    async (pageNumber: number, canvas: HTMLCanvasElement, cssWidth: number, pixelScale = 1) => {
      // Read here, not before the queue wait: the file may have been swapped
      // out (or the component unmounted) while this render was queued.
      const doc = docRef.current;
      if (!doc) throw new Error("PdfProvenance: PDF not ready");
      const page = await doc.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = cssWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      // Render at devicePixelRatio for crisp text on retina displays: the
      // canvas's CSS size stays at the fitted size, its backing buffer is
      // scaled up by dpr, and the drawing context is scaled to match so
      // pdf.js's own coordinate math (in CSS-scale units) is unaffected.
      const deviceRatio = window.devicePixelRatio || 1;
      const dpr =
        deviceRatio *
        capPixelScale(viewport.width, viewport.height, deviceRatio, pixelScale);
      canvas.width = Math.ceil(viewport.width * dpr);
      canvas.height = Math.ceil(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("PdfProvenance: canvas 2D context unavailable");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const task = page.render({ canvasContext: ctx, viewport });
      renderTasksRef.current.set(canvas, task);
      try {
        await task.promise;
      } finally {
        if (renderTasksRef.current.get(canvas) === task) renderTasksRef.current.delete(canvas);
      }

      return { width: viewport.width, height: viewport.height };
    },
    []
  );

  const renderPage = useCallback(
    (pageNumber: number, canvas: HTMLCanvasElement, cssWidth: number, pixelScale = 1) => {
      const seq = (renderSeqRef.current.get(canvas) ?? 0) + 1;
      renderSeqRef.current.set(canvas, seq);

      // Stop whatever is painting into this canvas right now. Anything
      // merely queued behind it will bail on the seq check below.
      renderTasksRef.current.get(canvas)?.cancel();

      const prior = renderQueueRef.current.get(canvas) ?? Promise.resolve();
      const run = prior.then(() => {
        if (renderSeqRef.current.get(canvas) !== seq) {
          // Superseded while queued. Callers already ignore render
          // rejections; the newer render is the one that reports a size.
          throw new Error("PdfProvenance: render superseded");
        }
        return doRenderPage(pageNumber, canvas, cssWidth, pixelScale);
      });
      // The chain must never stay rejected, or every later render into this
      // canvas would inherit the failure.
      renderQueueRef.current.set(
        canvas,
        run.catch(() => undefined)
      );
      return run;
    },
    [doRenderPage]
  );

  const getTextItems = useCallback(async (pageNumber: number) => {
    const cached = textCacheRef.current.get(pageNumber);
    if (cached) return cached;
    const doc = docRef.current;
    const pdfjs = pdfjsRef.current;
    if (!doc || !pdfjs) return [];
    const page = await doc.getPage(pageNumber);
    const items = await extractTextItems(page, pdfjs);
    textCacheRef.current.set(pageNumber, items);
    return items;
  }, []);

  return { status, pageCount, hasTextLayer, renderPage, getTextItems };
}

/**
 * True when `err` (a rejection from `renderPage`'s returned promise)
 * represents a render being superseded/cancelled as part of normal
 * operation — a resize, a page/thumbnail navigation, or a newer render
 * request arriving before an in-flight or still-queued one settles — rather
 * than an actual rendering failure. Two distinct pdf.js/queue mechanisms
 * produce this, both meaning "a newer request for this canvas already
 * exists":
 *   - `RenderingCancelledException`, thrown by pdf.js itself when a render
 *     already painting into the canvas has `task.cancel()` called on it
 *     (see `renderPage` above);
 *   - the local "render superseded" Error, thrown by the queue when a
 *     request that was still waiting its turn finds a newer seq has already
 *     landed by the time it's up.
 * Callers (index.tsx's per-page render effect) should never surface either
 * of these to the user — only a rejection that is NEITHER of these, and
 * therefore came from the latest requested render actually failing to
 * paint, warrants an inline "couldn't render" message.
 */
export function isRenderCancellation(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; message?: string };
  return e.name === "RenderingCancelledException" || e.message === "PdfProvenance: render superseded";
}
